import React, { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { Crosshair } from "@/components/Crosshair";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { ShieldCheck, Wallet } from "lucide-react";
import { usePageContent, AccentText } from "@/lib/pageContent";

// FAQ sections. Each section is anchor-linkable (/faq#<id>). The Verification & Trust
// copy is LOCKED per spec — the "Who actually sees my ID?" answer in particular must not
// be reworded.
const SECTIONS = [
  {
    id: "verification",
    label: "Verification & Trust",
    icon: ShieldCheck,
    items: [
      {
        q: "Why do I have to verify my ID?",
        a: "Optics are high value, easy to ship, and hard to authenticate from a photo, which makes them a favorite target for scammers. Every marketplace in this space has the same problem — a seller who doesn't exist, a listing that looks fine, and a buyer who never gets a package. Verification is what stops that.",
      },
      {
        q: "Who actually sees my ID?",
        a: "Your ID goes directly to Stripe, our payments provider. OpticSwap never receives or stores the image of your document. We're told pass or fail, your legal name, and nothing else.",
      },
      {
        q: "How long does it take?",
        a: "About two minutes on your phone. You'll photograph the front and back of a government photo ID, then take a selfie so Stripe can match your face to the document. Most checks clear in under a minute.",
      },
      {
        q: "What ID can I use?",
        a: "A driver's license, state ID, or passport. Photos have to be taken with your camera — uploads are disabled, which is what stops someone using a picture of someone else's license.",
      },
      {
        q: "What if it fails?",
        a: "Almost always it's the photo, not you. Try again in brighter light, hold steady, and get all four corners in frame. You get three attempts; after that an admin reviews it manually and you won't be locked out.",
      },
      {
        q: "Do I have to verify to buy?",
        a: "Yes. Buying requires a verified ID — the same two-minute Stripe check that sellers and payout recipients complete. Browsing the feed is open to members, but buying, selling, and messaging all happen inside a verified membership. That's what keeps both sides of every deal accountable.",
      },
      {
        q: "What does the colored bar on a listing mean?",
        a: "It shows what's been verified about the seller. Green means a verified person. Blue means a verified licensed dealer. Both means a verified person at a licensed business. No bar means not verified yet.",
      },
      {
        q: "What's the difference between ID Verified and Dealer Verified?",
        a: "ID Verified means a real, documented person is behind the account. Dealer Verified means the business itself is licensed and its FFL has been checked against the federal registry. Different claims, different badges.",
      },
      {
        q: "Does my verification expire?",
        a: "Identity verification doesn't. Dealer verification is tied to your FFL, so it lapses when the license does — we'll remind you 60 days out and again at 30.",
      },
      {
        q: "What does it cost?",
        a: "Nothing. Verification is free and always will be.",
      },
    ],
  },
  {
    id: "membership",
    label: "Membership & Fees",
    icon: Wallet,
    items: [
      {
        q: "Where does my membership fee go?",
        a: "Running the platform costs money whether anyone's actively buying or selling — ID verification, secure payment processing, keeping funds held safely until delivery confirms, hosting, all of it. The membership fee covers that baseline so your account stays verified and protected even in a slow month. Buy or sell a couple times and it pays for itself many times over in what you're actually protected against.",
      },
    ],
  },
];

function FaqSection({ section, sectionRef }) {
  const Icon = section.icon;
  return (
    <section
      id={section.id}
      ref={sectionRef}
      data-testid={`faq-section-${section.id}`}
      style={{ scrollMarginTop: 96 }}
      className="mt-12 first:mt-0"
    >
      <div className="flex items-center gap-2.5">
        <span className="inline-block w-1 self-stretch bg-[#4e8a1f]" style={{ minHeight: 28 }} aria-hidden="true" />
        {Icon && <Icon className="w-6 h-6 text-[#4e8a1f]" strokeWidth={1.8} />}
        <h2 className="font-heading text-2xl sm:text-3xl font-bold uppercase tracking-tight">{section.label}</h2>
      </div>
      <Crosshair className="my-5" />
      <Accordion type="single" collapsible className="max-w-2xl" data-testid={`faq-accordion-${section.id}`}>
        {section.items.map((it, i) => (
          <AccordionItem key={i} value={`${section.id}-${i}`} className="border-sniper">
            <AccordionTrigger
              data-testid={`faq-q-${section.id}-${i}`}
              className="font-heading uppercase text-left hover:text-volt"
            >
              {it.q}
            </AccordionTrigger>
            <AccordionContent className="font-body text-secondary-foreground leading-relaxed">
              {it.a}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </section>
  );
}

export default function FAQ() {
  const { get } = usePageContent("faq");
  const location = useLocation();
  const refs = useRef({});

  // Deep-link: /faq#verification scrolls to (and reveals) that section on load / hash change.
  useEffect(() => {
    const hash = (location.hash || "").replace("#", "");
    if (!hash) {
      window.scrollTo({ top: 0 });
      return;
    }
    const el = refs.current[hash];
    if (el) {
      // let layout settle before scrolling
      const t = setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
      return () => clearTimeout(t);
    }
  }, [location.hash]);

  return (
    <div className="min-h-screen text-foreground" data-testid="faq-page">
      <Navbar />
      <div className="max-w-[900px] mx-auto px-4 py-12">
        <div className="animate-fade-up">
          <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-volt">{get("eyebrow", "Help Center")}</span>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter mt-3">
            <AccentText text={get("heading", "FREQUENTLY ASKED\n*QUESTIONS*")} />
          </h1>
          <p className="mt-4 text-base text-secondary-foreground font-body max-w-xl">
            {get("subhead", "Straight answers about how OpticSwap keeps this marketplace honest — verification, trust, and what actually happens with your information.")}
          </p>
        </div>

        <div className="mt-10">
          {SECTIONS.map((section) => (
            <FaqSection
              key={section.id}
              section={section}
              sectionRef={(el) => { refs.current[section.id] = el; }}
            />
          ))}
        </div>
        <div className="h-16" />
      </div>
    </div>
  );
}
